import { useEffect, useMemo, useState } from 'react';

import { useQueries } from '@tanstack/react-query';

import rateGroupService, { RateByGroup, UngroupedRateDetails } from '~/services/cruises/RateGroupService';

import { rateGroupQueryKeys } from './rateGroupQueryKeys';

const MAX_CONCURRENT_DETAILS_REQUESTS = 3;

type DetailsBatch = {
  departureId: string;
  rateIds: Array<string>;
};

function buildDetailsBatches(rates: ReadonlyArray<RateByGroup>): Array<DetailsBatch> {
  const rateIdsByDepartureId = new Map<string, Array<string>>();

  rates.forEach((rate) => {
    if (!rate.departureId) {
      return;
    }

    const rateIds = rateIdsByDepartureId.get(rate.departureId) ?? [];
    rateIds.push(rate.id);
    rateIdsByDepartureId.set(rate.departureId, rateIds);
  });

  return Array.from(rateIdsByDepartureId, ([departureId, rateIds]) => ({ departureId, rateIds }));
}

export function useGetUngroupedRateDetails(
  vendorId: string | undefined,
  rates: ReadonlyArray<RateByGroup>,
  enabled = true,
) {
  const batches = useMemo(() => buildDetailsBatches(rates), [rates]);
  const batchSignature = batches.map(({ departureId, rateIds }) => `${departureId}:${rateIds.join(',')}`).join('|');
  const [state, setState] = useState({ batchSignature: '', enabledBatchCount: MAX_CONCURRENT_DETAILS_REQUESTS });
  const enabledBatchCount =
    state.batchSignature === batchSignature ? state.enabledBatchCount : MAX_CONCURRENT_DETAILS_REQUESTS;
  const queries = useQueries({
    queries: batches.map((batch, index) => ({
      queryKey: rateGroupQueryKeys.ungroupedRateDetails(vendorId, batch.departureId, batch.rateIds),
      queryFn: async () => {
        const response = await rateGroupService.getUngroupedRateDetails({
          vendorId,
          departureId: batch.departureId,
          rateIds: batch.rateIds,
        });
        return response.result;
      },
      enabled: enabled && !!vendorId && index < enabledBatchCount,
      retry: false,
      staleTime: Infinity,
    })),
  });
  const completedBatchCount = queries.filter((query) => query.isSuccess || query.isError).length;

  useEffect(() => {
    const nextEnabledBatchCount = Math.min(batches.length, MAX_CONCURRENT_DETAILS_REQUESTS + completedBatchCount);

    if (state.batchSignature !== batchSignature || state.enabledBatchCount !== nextEnabledBatchCount) {
      setState({ batchSignature, enabledBatchCount: nextEnabledBatchCount });
    }
  }, [batchSignature, batches.length, completedBatchCount, state]);

  const detailsByRateId = useMemo(() => {
    const details = new Map<string, UngroupedRateDetails>();
    queries.forEach((query) => query.data?.forEach((detail) => details.set(detail.id, detail)));
    return details;
  }, [queries]);

  return {
    detailsByRateId,
    isLoadingByDepartureId: new Map(
      batches.map((batch, index) => [batch.departureId, enabled && (queries[index]?.isPending ?? false)]),
    ),
    isErrorByDepartureId: new Map(batches.map((batch, index) => [batch.departureId, queries[index]?.isError ?? false])),
    isSettled: !enabled || queries.every((query) => query.isSuccess || query.isError),
  };
}
